import {
  buildAppVisualReferencesForAi,
} from './buildAppVisualReferencesForAi';
import {
  getCoverAiAttachmentUrlsFromSelection,
  DEFAULT_TEMPLATE_FILENAME,
} from './aiAssistantReferenceImages';
import {
  buildCoverImageRulesBlock,
  buildPublicImageUrlNotice,
  buildReferenceImageAttachmentNotice,
  buildTemplateCloneInstructions,
  formatPublicImageUrlLine,
  formatSecondaryBrandContextBlock,
} from './coverImageAiPromptShared';

function formatCatalogModuleCoversSection(covers, selectedReferenceUrl = null) {
  const lines = covers.map((cover) => {
    const selectedMark =
      selectedReferenceUrl && cover.url === selectedReferenceUrl ? ' ← TEMPLATE MASTER (anexado)' : '';
    return `- Módulo: ${cover.moduleTitle} — Categoria: ${cover.categoryTitle}\n${formatPublicImageUrlLine(cover.url)}${selectedMark}`;
  });

  if (!lines.length) {
    return `Capas de módulos do catálogo deste produto:
(nenhuma capa de módulo encontrada)`;
  }

  return `Capas de módulos do catálogo deste produto (referência de estilo):
${lines.join('\n')}`;
}

function buildProductCoverTextRulesBlock() {
  return `Regras da capa (OBRIGATÓRIO):
- Proporção 16:9 (horizontal)
- TEXTO NA CAPA — permitido SOMENTE:
  (a) o título do produto, EXATAMENTE como indicado abaixo — em destaque, legível
  (b) NO MÁXIMO mais um elemento curto: OU uma tag pequena com a categoria de produtos (1–3 palavras) OU um ícone minimalista app-style — nunca os dois junto com textos extras
- PROIBIDO na capa: subtítulos, frases de marketing, descrições longas, listas, "Programa", slogans, URLs, preços ou qualquer texto além do título + (opcional) tag OU ícone
- Estilo: tile de aplicativo / vitrine de streaming — limpo, poucos elementos, hierarquia clara
- Se puder gerar imagens: entregue UM arquivo 16:9 pronto para upload no painel
- Se NÃO puder gerar imagens: descreva brief detalhado (composição, cores, tipografia, elementos)`;
}

function isCloneReferenceKind(kind) {
  return kind === 'catalog-module';
}

/**
 * Prompt para capa 16:9 de UM produto na vitrine — gerar por produto, não em lote.
 */
export function buildProductCoverAiAssistantPayload({
  productTitle = '',
  productCategoryName = null,
  catalogProducts = [],
  catalogModuleCovers = [],
  excludeProductId = '',
  equivalentIds = [],
  visualContext = {},
  selectedReference = null,
} = {}) {
  const resolvedTitle = productTitle || '(sem título)';
  const appVisualReferences = buildAppVisualReferencesForAi(visualContext);
  const brandBlock = formatSecondaryBrandContextBlock(appVisualReferences);

  const moduleCovers = (catalogModuleCovers || []).map((cover) => ({
    moduleTitle: cover.label || cover.moduleTitle || '(sem título)',
    categoryTitle: cover.categoryTitle || '(sem categoria)',
    url: cover.url,
  }));

  const templateMasterUrl = selectedReference?.url || null;
  const strictSiblingTemplate =
    isCloneReferenceKind(selectedReference?.kind) && !!templateMasterUrl;
  const siblingCountForClone = strictSiblingTemplate
    ? selectedReference?.kind === 'catalog-module'
      ? 1
      : Math.max(moduleCovers.length, 1)
    : 0;
  const cloneScopeLabel = 'catálogo deste produto';

  const attachmentUrls = selectedReference
    ? getCoverAiAttachmentUrlsFromSelection(selectedReference)
    : [];
  const attachmentFilename = attachmentUrls[0]?.filename || DEFAULT_TEMPLATE_FILENAME;
  const hasAttachment = attachmentUrls.length > 0;

  const referenceNotice = hasAttachment
    ? buildReferenceImageAttachmentNotice({
        attachmentFilename,
        siblingCount: siblingCountForClone,
      })
    : buildPublicImageUrlNotice();

  const cloneBlock =
    strictSiblingTemplate && templateMasterUrl
      ? buildTemplateCloneInstructions({
          coverTitle: resolvedTitle,
          templateMasterUrl,
          siblingCount: siblingCountForClone,
          scopeLabel: cloneScopeLabel,
          attachmentFilename,
          selectedReferenceLabel: selectedReference?.label,
        })
      : '';

  const moduleBlock = formatCatalogModuleCoversSection(moduleCovers, templateMasterUrl);
  const referencesBlock = [moduleBlock, brandBlock].filter(Boolean).join('\n\n');

  const categoryLine = productCategoryName
    ? `Categoria de produtos: ${productCategoryName}`
    : 'Categoria de produtos: (nenhuma selecionada ainda — tag opcional na capa)';

  const tagHint = productCategoryName
    ? `Tag opcional na capa: "${productCategoryName}" (1–3 palavras) OU ícone minimalista — nunca os dois`
    : 'Tag opcional na capa: ícone minimalista app-style — ou nenhum dos dois';

  const rulesBlock = hasAttachment
    ? buildCoverImageRulesBlock({
        aspectRatioLabel: '16:9 (horizontal — tile da vitrine de produtos)',
        subjectLabel: `produto na vitrine: "${resolvedTitle}"`,
        referenceLabel: 'vitrine',
        coverTitle: resolvedTitle,
        coverTitleLabel: 'Título do produto na capa',
        strictSiblingTemplate,
        attachmentFilename,
      })
    : buildProductCoverTextRulesBlock();

  const selectedNote = selectedReference?.label
    ? `\nCapa de referência escolhida pelo admin: "${selectedReference.label}"${
        selectedReference.kind === 'catalog-module' ? ' (módulo do catálogo deste produto)' : ''
      }\n`
    : '';

  const text = `${referenceNotice}

Preciso criar a capa 16:9 de UM produto na vitrine do app (gerar só esta capa — não é lote).
${selectedNote}${cloneBlock ? `\n${cloneBlock}\n` : ''}
Título do produto (OBRIGATÓRIO na capa — copiar exatamente):
${resolvedTitle}

${categoryLine}
${tagHint}

Referências visuais complementares (URLs de backup${hasAttachment ? ` — a imagem anexada "${attachmentFilename}" tem prioridade` : ''}):
${referencesBlock}

${rulesBlock}

Formato da resposta (obrigatório, em português):

0) ${hasAttachment ? `A imagem "${attachmentFilename}" está anexada nesta conversa e você conseguiu analisá-la?` : 'Conseguiu usar as referências visuais disponíveis?'}
(sim / não — se não, peça o anexo ou mais referências antes de gerar; NÃO invente estilo)

1) Capa sugerida:
(gerada / brief para produção)
Texto na capa: "${resolvedTitle}" + (tag OU ícone — ou nenhum)

2) Como seguiu as referências:
- Paleta/cores usadas: …
- Elementos replicados das capas de referência: …

3) Observações (opcional):
…

O admin fará upload manual desta capa em Produtos → editar → Vitrine de Produtos → Imagem da vitrine.`;

  return { text, attachmentUrls };
}

export function buildProductCoverAiAssistantText(options = {}) {
  return buildProductCoverAiAssistantPayload(options).text;
}
